/**
 * arbi docs                        — list documents (with filtering & output options)
 * arbi doc get [ids...]            — get doc details (picker if no IDs)
 * arbi doc delete [ids...]         — delete docs (picker if no IDs)
 * arbi doc update [json]           — update docs (form if no JSON)
 * arbi doc upload-url <urls...>    — upload from URLs
 * arbi doc parsed <doc-id> [stage] — parsed content (stage defaults to 'final')
 * arbi doc summary                 — status summary counts
 */

import { writeFileSync } from 'node:fs'
import { Command } from 'commander'
import { documents, connectWithReconnect, workspaces } from '@arbidocs/sdk'
import type { DocumentListFields } from '@arbidocs/sdk'
import { isMessageType, type WsTaskUpdateMessage } from '@arbidocs/client'
import { runAction, resolveWorkspace, printTable, parseJsonArg, dryRun } from '../helpers.js'
import { getConfig } from '../config.js'
import { success, error, warn, dim, chalk } from '../output.js'
import { promptSearch, promptCheckbox, promptInput, promptSelect } from '../prompts.js'

export function statusSymbol(status: string | null | undefined): string {
  switch (status) {
    case 'completed':
      return '\u2713' // ✓
    case 'failed':
      return '\u2717' // ✗
    case 'skipped':
      return '\u2013' // –
    case 'queued':
      return '\u25CB' // ○
    default:
      return '\u25CF' // ● (processing, parsing, indexing, etc.)
  }
}

export function statusColor(status: string | null | undefined): (s: string) => string {
  switch (status) {
    case 'completed':
      return chalk.green
    case 'failed':
      return chalk.red
    case 'queued':
      return chalk.yellow
    case 'processing':
    case 'parsing':
    case 'indexing':
    case 'analysing':
    case 'encrypting':
      return chalk.cyan
    default:
      return (s: string) => s
  }
}

export function formatSize(bytes: number | null | undefined): string {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}K`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}M`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}G`
}

/** Apply client-side filters to a document list */
export function filterDocs(
  data: Record<string, unknown>[],
  opts: { status?: string; folder?: string; ext?: string; query?: string }
): Record<string, unknown>[] {
  let result = data
  if (opts.status) {
    const statuses = opts.status.split(',')
    result = result.filter((d) => statuses.includes(d.status as string))
  }
  if (opts.folder) {
    const pattern = opts.folder.toLowerCase()
    result = result.filter((d) => ((d.folder as string) ?? '').toLowerCase().includes(pattern))
  }
  if (opts.ext) {
    const exts = opts.ext
      .split(',')
      .map((e) => (e.startsWith('.') ? e.toLowerCase() : `.${e.toLowerCase()}`))
    result = result.filter((d) => {
      const fn = (d.file_name as string) ?? ''
      const dotIdx = fn.lastIndexOf('.')
      const ext = dotIdx >= 0 ? fn.slice(dotIdx).toLowerCase() : ''
      return exts.includes(ext)
    })
  }
  if (opts.query) {
    const q = opts.query.toLowerCase()
    result = result.filter((d) => {
      const fn = ((d.file_name as string) ?? '').toLowerCase()
      const meta = d.doc_metadata as { title?: string | null; doc_subject?: string | null } | null
      const title = (meta?.title ?? '').toLowerCase()
      const subject = (meta?.doc_subject ?? '').toLowerCase()
      return fn.includes(q) || title.includes(q) || subject.includes(q)
    })
  }
  return result
}

/** Fetch workspace docs and return as picker choices */
async function fetchDocChoices(arbi: Awaited<ReturnType<typeof resolveWorkspace>>['arbi']) {
  // Use lite mode for the picker — we only need file_name, status, external_id
  const data = await documents.listAll(arbi, { fields: 'lite', order: 'created_desc' })
  if (data.length === 0) {
    console.log('No documents found.')
    process.exit(0)
  }
  return data.map((d) => ({
    name: `${d.file_name ?? 'Unnamed'} (${d.status})`,
    value: d.external_id as string,
    description: d.external_id as string,
  }))
}

interface DocsListOpts {
  workspace?: string
  json?: boolean
  csv?: boolean
  ids?: boolean
  count?: boolean
  status?: string
  folder?: string
  ext?: string
  query?: string
  sort?: string
  limit?: string
  output?: string
  lite?: boolean
}

export function registerDocsCommand(program: Command): void {
  program
    .command('docs')
    .description('List documents in the active workspace')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('--json', 'Output as JSON')
    .option('--csv', 'Output as CSV')
    .option('--ids', 'Output only document IDs (one per line)')
    .option('--count', 'Output only the count (combine with --status for filtered count)')
    .option('-s, --status <status>', 'Filter by status (comma-separated: completed,failed,queued)')
    .option('-f, --folder <pattern>', 'Filter by folder (substring match)')
    .option('-e, --ext <extensions>', 'Filter by file extension (comma-separated: pdf,docx,eml)')
    .option('-q, --query <text>', 'Search file name, title, or summary')
    .option('--sort <field>', 'Sort by field (name, status, date, size, created)', 'status')
    .option(
      '-n, --limit <n>',
      'Stop after N results. Fetching stops early — no unnecessary pages are loaded.'
    )
    .option(
      '--lite',
      'Use lite field mode: skips per-doc decrypt and returns null metadata and empty doctags. ' +
        'Much faster on large workspaces; fetch full doc details on demand.'
    )
    .option(
      '-o, --output <path>',
      'Write output to a file instead of stdout. Extension determines the format when combined with --ids/--json/--csv (default: csv with doc_id, file_name, folder, status)'
    )
    .action((opts: DocsListOpts) =>
      runAction(async () => {
        const { arbi, workspaceId } = await resolveWorkspace(opts.workspace)

        // Fetch workspace metadata (name) so callers don't need a second
        // ``arbi workspace current`` round-trip just to label the result.
        // This is one extra GET /v1/user/workspaces per ``arbi docs`` —
        // cheap, RLS-filtered, and the agent saves a step. Failures are
        // non-fatal: we just omit the name.
        let workspaceName: string | null = null
        try {
          const list = await workspaces.listWorkspaces(arbi)
          workspaceName = list.find((w) => w.external_id === workspaceId)?.name ?? null
        } catch {
          // Network blip or permission shape we don't expect — proceed
          // without the name rather than failing the whole command.
        }

        const fields: DocumentListFields | undefined = opts.lite ? 'lite' : undefined
        const hardLimit = opts.limit ? parseInt(opts.limit, 10) : undefined

        // Collect rows progressively. We stream output as pages arrive in table/ids/csv
        // modes (no --output) so the terminal updates immediately.
        // For json/--output/count/sort modes we still buffer all pages first since those
        // outputs require the complete set.
        const needsSort = !!(opts.sort && opts.sort !== 'status')
        const bufferAll = !!(opts.json || opts.output || opts.count || needsSort)

        const data: Record<string, unknown>[] = []
        let totalFetched = 0

        const csvEscape = (s: string | null | undefined) => `"${(s ?? '').replace(/"/g, '""')}"`

        const csvHeader =
          'external_id,status,file_name,file_size,folder,n_pages,tokens,doc_date,title'

        const renderRow = (d: Record<string, unknown>): string => {
          const meta = d.doc_metadata as {
            title?: string | null
            doc_date?: string | null
          } | null
          return [
            d.external_id,
            d.status,
            csvEscape((d.file_name as string) ?? ''),
            d.file_size ?? '',
            csvEscape((d.folder as string) ?? ''),
            d.n_pages ?? '',
            d.tokens ?? '',
            meta?.doc_date ?? '',
            csvEscape(meta?.title ?? ''),
          ].join(',')
        }

        // Print CSV header eagerly when streaming
        if (!bufferAll && (opts.csv || (!opts.ids && !opts.json && !opts.output))) {
          if (opts.csv) process.stdout.write(csvHeader + '\n')
        }

        for await (const page of documents.listPaginated(arbi, {
          pageSize: 500,
          order: 'created_desc',
          fields,
        })) {
          let filtered = filterDocs(page as Record<string, unknown>[], opts)

          // Apply early limit: stop once we have enough
          if (hardLimit !== undefined) {
            const remaining = hardLimit - totalFetched
            if (remaining <= 0) break
            filtered = filtered.slice(0, remaining)
          }

          if (filtered.length === 0) {
            // Page may be entirely filtered out; check if we hit the limit
            if (hardLimit !== undefined && totalFetched >= hardLimit) break
            continue
          }

          totalFetched += filtered.length

          if (bufferAll) {
            data.push(...filtered)
          } else {
            // Streaming output — print rows as they arrive
            if (opts.ids) {
              filtered.forEach((d) => process.stdout.write((d.external_id as string) + '\n'))
            } else if (opts.csv) {
              filtered.forEach((d) => process.stdout.write(renderRow(d) + '\n'))
            } else {
              // Table mode — buffer page for printTable (can't stream individual rows
              // without column alignment, so we reprint per page)
              data.push(...filtered)
            }
          }

          if (hardLimit !== undefined && totalFetched >= hardLimit) break
        }

        if (totalFetched === 0 && data.length === 0) {
          // Machine-readable modes: emit a valid-but-empty payload so scripts
          // can parse the output without special-casing the no-results path.
          if (opts.json) {
            const emptyPayload = JSON.stringify(
              {
                workspace: { external_id: workspaceId, name: workspaceName },
                documents: [],
              },
              null,
              opts.output ? 2 : 0
            )
            if (opts.output) writeFileSync(opts.output, emptyPayload + '\n')
            else console.log(emptyPayload)
          } else if (opts.ids) {
            if (opts.output) writeFileSync(opts.output, '')
            // Streaming IDs mode: nothing to emit, stay silent (valid empty stream).
          } else if (opts.csv) {
            if (opts.output) writeFileSync(opts.output, csvHeader + '\n')
            // Streaming CSV already printed the header above.
          } else if (opts.count) {
            const wsLabel = workspaceName ? `${workspaceName} (${workspaceId})` : workspaceId
            console.log(chalk.dim(`Workspace: ${wsLabel}`))
            console.log('0')
          } else {
            const wsLabel = workspaceName ? `${workspaceName} (${workspaceId})` : workspaceId
            console.log(chalk.dim(`Workspace: ${wsLabel}`))
            console.log('No documents found.')
          }
          return
        }

        // For streaming non-table modes we're done — output already printed
        if (!bufferAll && (opts.ids || opts.csv)) {
          return
        }

        // Sort (buffered modes)
        if (bufferAll || (!opts.ids && !opts.csv)) {
          const sortField = opts.sort ?? 'status'
          data.sort((a, b) => {
            switch (sortField) {
              case 'name':
                return ((a.file_name as string) ?? '').localeCompare((b.file_name as string) ?? '')
              case 'size':
                return ((b.file_size as number) ?? 0) - ((a.file_size as number) ?? 0)
              case 'date':
              case 'created':
                return ((b.created_at as string) ?? '').localeCompare(
                  (a.created_at as string) ?? ''
                )
              case 'status':
              default:
                return ((a.status as string) ?? '').localeCompare((b.status as string) ?? '')
            }
          })
        }

        // -- Count mode --
        if (opts.count) {
          if (opts.status || opts.folder || opts.ext || opts.query) {
            console.log(String(data.length))
          } else {
            const counts: Record<string, number> = {}
            data.forEach((d) => {
              const s = (d.status as string) ?? 'unknown'
              counts[s] = (counts[s] ?? 0) + 1
            })
            const withSummary = data.filter((d) => {
              const m = d.doc_metadata as { doc_subject?: string | null } | null
              return m?.doc_subject
            }).length
            const totalPages = data.reduce((sum, d) => sum + ((d.n_pages as number) ?? 0), 0)
            const totalTokens = data.reduce((sum, d) => sum + ((d.tokens as number) ?? 0), 0)
            // Include workspace context so callers (especially agents)
            // can answer "how many docs in the workspace" in one shot.
            const wsLabel = workspaceName ? `${workspaceName} (${workspaceId})` : workspaceId
            console.log(chalk.dim(`Workspace: ${wsLabel}`))
            console.log(chalk.bold(`Total: ${data.length} documents`))
            for (const [status, count] of Object.entries(counts).sort()) {
              const colorFn = statusColor(status)
              const pct = ((count / data.length) * 100).toFixed(1)
              console.log(`  ${colorFn(statusSymbol(status))} ${status}: ${count} (${pct}%)`)
            }
            console.log(`  With summaries: ${withSummary}`)
            console.log(`  Total pages: ${totalPages.toLocaleString()}`)
            console.log(`  Total tokens: ${totalTokens.toLocaleString()}`)
          }
          return
        }

        // Small helper: either write the payload to opts.output (if set)
        // or print it to stdout.
        const writeOut = (payload: string, defaultExt: string) => {
          if (opts.output) {
            writeFileSync(opts.output, payload.endsWith('\n') ? payload : payload + '\n')
            success(`Wrote ${data.length} rows to ${opts.output} (${defaultExt})`)
          } else {
            console.log(payload.endsWith('\n') ? payload.slice(0, -1) : payload)
          }
        }

        // -- IDs mode (buffered - writing to file) --
        if (opts.ids) {
          writeOut(data.map((d) => d.external_id as string).join('\n'), 'ids')
          return
        }

        // -- JSON mode --
        if (opts.json) {
          // Wrap with workspace context so a single ``arbi docs --json``
          // tells the caller both *what* docs and *which* workspace —
          // saves agents (and any scripted consumer) from a second
          // ``arbi workspace current`` round-trip. Existing scripts
          // expecting a bare array need to read ``.documents`` now.
          const payload = {
            workspace: { external_id: workspaceId, name: workspaceName },
            documents: data,
          }
          writeOut(JSON.stringify(payload, null, opts.output ? 2 : 0), 'json')
          return
        }

        // -- CSV mode (buffered - writing to file) --
        if (opts.csv || (opts.output && !opts.ids && !opts.json)) {
          const lines = [csvHeader]
          for (const d of data) {
            lines.push(renderRow(d))
          }
          writeOut(lines.join('\n'), 'csv')
          return
        }

        // -- Table mode (default) --
        const wsLabel = workspaceName ? `${workspaceName} (${workspaceId})` : workspaceId
        console.log(chalk.dim(`Workspace: ${wsLabel}`))
        console.log(chalk.dim(`${data.length} documents\n`))
        printTable(
          [
            { header: 'ID', width: 24, value: (r) => r.external_id as string },
            {
              header: 'S',
              width: 4,
              value: (r) => {
                const s = r.status as string | null
                return statusColor(s)(statusSymbol(s))
              },
            },
            {
              header: 'TITLE',
              width: 34,
              value: (r) => {
                const meta = r.doc_metadata as { title?: string | null } | null
                return meta?.title ?? (r.file_name as string) ?? 'Unnamed'
              },
            },
            {
              header: 'SIZE',
              width: 8,
              value: (r) => formatSize(r.file_size as number | null),
            },
            {
              header: 'PAGES',
              width: 6,
              value: (r) => (r.n_pages ? String(r.n_pages) : '-'),
            },
            {
              header: 'DATE',
              width: 12,
              value: (r) => {
                const meta = r.doc_metadata as { doc_date?: string | null } | null
                return meta?.doc_date ?? '-'
              },
            },
            {
              header: 'FOLDER',
              width: 30,
              value: (r) => (r.folder as string) ?? '-',
            },
          ],
          data
        )
      })()
    )

  const doc = program.command('doc').description('Document management')

  doc
    .command('get [ids...]')
    .description('Get document details (interactive picker if no IDs given)')
    .action((ids?: string[]) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        let docIds = ids && ids.length > 0 ? ids : undefined
        if (!docIds) {
          const choices = await fetchDocChoices(arbi)
          const selected = await promptCheckbox('Select documents', choices)
          if (selected.length === 0) return
          docIds = selected
        }
        const data = await documents.getDocuments(arbi, docIds)
        console.log(JSON.stringify(data, null, 2))

        // Surface processing errors for failed documents.
        // The backend may include error details in various fields that aren't
        // in the typed schema, so we check the raw object dynamically.
        const docs = Array.isArray(data) ? data : [data]
        for (const d of docs) {
          const raw = d as Record<string, unknown>
          if (raw.status === 'failed') {
            const reason =
              (raw.error_reason as string) ||
              (raw.status_details as string) ||
              (raw.error as string) ||
              null
            const docId = raw.external_id as string
            if (reason) {
              console.error(chalk.red(`\n⚠ ${docId} processing failed: ${reason}`))
            } else {
              console.error(
                chalk.red(`\n⚠ ${docId} processing failed (no error details available)`)
              )
            }
          }
        }
      })()
    )

  doc
    .command('delete [ids...]')
    .description('Delete documents (interactive picker if no IDs given)')
    .option('--dry-run', 'Preview which docs would be deleted (no SDK call)')
    .action((ids?: string[], opts?: { dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        let docIds = ids && ids.length > 0 ? ids : undefined
        if (!docIds) {
          const choices = await fetchDocChoices(arbi)
          const selected = await promptCheckbox('Select documents to delete', choices)
          if (selected.length === 0) return
          docIds = selected
        }
        if (opts?.dryRun) {
          dryRun(`delete ${docIds.length} document(s)`, docIds)
          return
        }
        await documents.deleteDocuments(arbi, docIds)
        success(`Deleted ${docIds.length} document(s).`)
      })()
    )

  // ── share / unshare ──────────────────────────────────────────────────────
  // Two named subcommands instead of forcing agents through
  // `doc update '[{"external_id":"…","shared":true}]'` — toggling one
  // boolean is the most-tripped-over JSON path in the CLI.
  doc
    .command('share <ids...>')
    .description('Mark documents as shared (visible to all workspace members)')
    .action((ids: string[]) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        await documents.updateDocuments(
          arbi,
          ids.map((external_id) => ({ external_id, shared: true }))
        )
        success(`Shared ${ids.length} document(s).`)
      })()
    )
  doc
    .command('unshare <ids...>')
    .description('Mark documents as private (owner-only)')
    .action((ids: string[]) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        await documents.updateDocuments(
          arbi,
          ids.map((external_id) => ({ external_id, shared: false }))
        )
        success(`Unshared ${ids.length} document(s).`)
      })()
    )

  // ── similar ──────────────────────────────────────────────────────────────
  // De-duplication primitive: find near-duplicate documents (same content,
  // different filenames) before deciding to ingest a new file. Pairs are
  // stored at upload time, so this is a cheap cached lookup, not a re-scan.
  doc
    .command('similar [doc-id]')
    .description('Find near-duplicate documents (cached similarity pairs)')
    .option(
      '--threshold <n>',
      'Minimum cosine similarity score, 0.0–1.0 (default 0.0 = all stored pairs)',
      (v) => parseFloat(v),
      0.0
    )
    .option('--json', 'Output as JSON')
    .action((docId: string | undefined, opts: { threshold: number; json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const res = await arbi.fetch.GET('/v1/document/similar', {
          params: {
            query: {
              ...(docId ? { doc_ext_id: docId } : {}),
              threshold: opts.threshold,
            },
          },
        })
        if (res.error) throw new Error(`similar failed: ${JSON.stringify(res.error)}`)
        // Endpoint returns `{workspace_ext_id, pairs: [...]}` — surface the
        // envelope to --json callers, but flatten to the pairs array for the
        // table view (pairs is what the human cares about).
        const envelope = res.data as {
          workspace_ext_id?: string
          pairs?: Array<Record<string, unknown>>
        }
        const pairs = envelope.pairs ?? []
        if (opts.json) {
          console.log(JSON.stringify(envelope, null, 2))
          return
        }
        if (pairs.length === 0) {
          process.stderr.write(
            docId
              ? `No similar documents found above threshold ${opts.threshold}.\n`
              : 'No stored similarity pairs.\n'
          )
          return
        }
        printTable(
          [
            { header: 'DOC A', width: 18, value: (r) => (r.doc_a_ext_id as string) ?? '' },
            { header: 'DOC B', width: 18, value: (r) => (r.doc_b_ext_id as string) ?? '' },
            {
              header: 'SCORE',
              width: 8,
              value: (r) => ((r.score as number) ?? 0).toFixed(3),
            },
          ],
          pairs
        )
      })()
    )

  doc
    .command('update [json]')
    .description('Update documents (interactive form if no JSON given)')
    .action((json?: string) =>
      runAction(async () => {
        if (json) {
          const parsed = parseJsonArg<
            Record<string, unknown>[] | { documents: Record<string, unknown>[] }
          >(json, 'arbi doc update \'[{"external_id": "doc-123", "shared": true}]\'')
          const docs = Array.isArray(parsed) ? parsed : parsed.documents

          const { arbi } = await resolveWorkspace()
          const data = await documents.updateDocuments(
            arbi,
            docs as Parameters<typeof documents.updateDocuments>[1]
          )
          success(`Updated ${data.length} document(s).`)
        } else {
          // Interactive: pick a doc, then ask what to change
          const { arbi } = await resolveWorkspace()
          const choices = await fetchDocChoices(arbi)
          const docId = await promptSearch('Select document to update', choices)

          const field = await promptSelect('Field to update', [
            { name: 'Title', value: 'title' },
            { name: 'Shared', value: 'shared' },
            { name: 'Author', value: 'author' },
            { name: 'Abstract', value: 'abstract' },
            { name: 'Date', value: 'date' },
          ])

          let value: unknown
          if (field === 'shared') {
            value = await promptSelect('Shared?', [
              { name: 'Yes', value: true },
              { name: 'No', value: false },
            ])
          } else if (field === 'date') {
            value = (await promptInput('Date (YYYY-MM-DD)', false)) || null
          } else {
            value = await promptInput(`${field.charAt(0).toUpperCase() + field.slice(1)}`)
          }

          const data = await documents.updateDocuments(arbi, [
            { external_id: docId, [field]: value },
          ])
          success(`Updated ${data.length} document(s).`)
        }
      })()
    )

  doc
    .command('upload-url <urls...>')
    .description('Upload documents from URLs')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('--shared', 'Make documents shared', false)
    .option('--json', 'Output results as JSON')
    .action((urls: string[], opts: { workspace?: string; shared?: boolean; json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = await documents.uploadUrl(arbi, urls, opts.shared ?? false)
        if (opts.json) {
          console.log(
            JSON.stringify({
              docIds: data.doc_ext_ids ?? [],
              skipped: data.skipped ?? [],
            })
          )
          return
        }
        success(`Uploaded: ${(data.doc_ext_ids ?? []).join(', ')}`)
        if (data.skipped && data.skipped.length > 0) {
          warn(
            `Skipped: ${data.skipped.map((s: { file_name: string; reason: string }) => `${s.file_name} (${s.reason})`).join(', ')}`
          )
        }
      })()
    )

  doc
    .command('parsed <doc-id> [stage]')
    .description('Get parsed document content (stage: marker, subchunk, final; default: final)')
    .action((docId: string, stage?: string) =>
      runAction(async () => {
        const { accessToken, config } = await resolveWorkspace()

        // Default to 'final' stage if not specified (most common use case)
        const validStages = ['marker', 'subchunk', 'final']
        const selectedStage = stage ?? 'final'
        if (!validStages.includes(selectedStage)) {
          error(`Invalid stage: ${selectedStage}. Must be one of: ${validStages.join(', ')}`)
          process.exit(1)
        }

        const data = await documents.getParsedContent(
          { baseUrl: config.baseUrl, accessToken },
          docId,
          selectedStage
        )
        console.log(JSON.stringify(data, null, 2))
      })()
    )

  doc
    .command('reprocess [ids...]')
    .description('Reprocess failed/completed documents (sets status back to processing)')
    .option('-s, --status <status>', 'Reprocess all docs with this status (e.g. failed)')
    .option('-f, --folder <pattern>', 'Filter by folder (substring match)')
    .option(
      '-e, --ext <extension>',
      'Filter by file extension (e.g. pdf, docx). Requires --status.'
    )
    .option('--dry-run', 'Show what would be reprocessed without doing it')
    .option('-b, --batch-size <n>', 'Batch size for update requests', '50')
    .option(
      '-i, --interval <seconds>',
      'Seconds to wait between batches (float allowed, e.g. 0.5). Default 0 — send as fast as the server acks.',
      '0'
    )
    .option(
      '-r, --max-retries <n>',
      'Retry attempts per failing batch before falling back to per-doc submission.',
      '3'
    )
    .option(
      '--status-interval <seconds>',
      'Seconds between progress lines (0 to disable periodic printing).',
      '5'
    )
    .option('-v, --verbose', 'Print a log line after each batch in addition to periodic stats.')
    .option('-q, --quiet', 'Suppress periodic progress lines — only print the final summary.')
    .option('-W, --watch', 'Watch document processing progress after submission')
    .option('--no-watch', 'Skip watching document processing')
    .action(
      (
        ids: string[] | undefined,
        opts: {
          status?: string
          folder?: string
          ext?: string
          dryRun?: boolean
          batchSize?: string
          interval?: string
          maxRetries?: string
          statusInterval?: string
          verbose?: boolean
          quiet?: boolean
          watch?: boolean
        }
      ) =>
        runAction(async () => {
          const isInteractive = process.stdout.isTTY === true
          const watchPref = getConfig()?.watch !== false
          const willWatch =
            opts.watch === false ? false : opts.watch === true || (watchPref && isInteractive)

          const { arbi, config, accessToken } = await resolveWorkspace(undefined, {
            skipNotifications: true,
          })
          let docIds: string[]
          const docNames = new Map<string, string>()

          if (ids && ids.length > 0) {
            if (opts.ext || opts.folder) {
              warn('--ext/--folder are ignored when explicit IDs are given.')
            }
            docIds = ids
          } else if (opts.status) {
            // Fetch all docs progressively and filter client-side.
            const allDocs = (await documents.listAll(arbi, {
              fields: 'lite',
              order: 'created_desc',
            })) as Record<string, unknown>[]
            let filtered = allDocs.filter((d) => d.status === opts.status)
            if (opts.folder) {
              const pattern = opts.folder.toLowerCase()
              filtered = filtered.filter((d) =>
                ((d.folder as string) ?? '').toLowerCase().includes(pattern)
              )
            }
            if (opts.ext) {
              // Match on file_name suffix; accept "pdf" or ".pdf".
              const ext = opts.ext.toLowerCase().replace(/^\./, '')
              filtered = filtered.filter((d) =>
                ((d.file_name as string) ?? '').toLowerCase().endsWith('.' + ext)
              )
            }
            docIds = filtered.map((d) => d.external_id as string)
            for (const d of filtered) {
              docNames.set(d.external_id as string, (d.file_name as string) ?? 'Unnamed')
            }
          } else {
            error('Provide document IDs or use --status to select documents to reprocess.')
            process.exit(1)
            return
          }

          if (docIds.length === 0) {
            console.log('No documents match the criteria.')
            return
          }

          console.log(`${docIds.length} document(s) to reprocess.`)

          if (opts.dryRun) {
            docIds.forEach((id) => console.log(`  ${id}`))
            console.log(chalk.dim('\n(dry run — no changes made)'))
            return
          }

          const batchSize = parseInt(opts.batchSize ?? '50', 10)
          const maxRetries = parseInt(opts.maxRetries ?? '3', 10)
          const intervalSec = parseFloat(opts.interval ?? '0')
          const intervalMs =
            Number.isFinite(intervalSec) && intervalSec > 0 ? intervalSec * 1000 : 0
          const statusIntervalSec = parseFloat(opts.statusInterval ?? '5')
          const statusIntervalMs =
            Number.isFinite(statusIntervalSec) && statusIntervalSec > 0
              ? statusIntervalSec * 1000
              : 0

          const total = docIds.length
          const startMs = Date.now()
          let submitted = 0
          let succeeded = 0
          let failed = 0
          const failedIds: string[] = []

          const formatDuration = (ms: number): string => {
            const s = Math.floor(ms / 1000)
            if (s < 60) return `${s}s`
            const m = Math.floor(s / 60)
            if (m < 60) return `${m}m${s % 60}s`
            const h = Math.floor(m / 60)
            return `${h}h${m % 60}m`
          }

          // Local HH:MM:SS for the status-line timestamp. Tying each
          // line to a wall-clock makes `elapsed=` sanity-checkable in
          // a running tail and lets the operator cross-reference with
          // other logs.
          const formatClock = (ts: number): string => {
            const d = new Date(ts)
            const pad = (n: number): string => n.toString().padStart(2, '0')
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
          }

          // ── Processing watch state (populated when --watch) ──────────
          let procCompleted = 0
          let procFailed = 0
          const procPending = new Set<string>()
          const procFailedDetails = new Map<string, string>()

          const printStatus = (prefix = ''): void => {
            const now = Date.now()
            const elapsed = now - startMs
            const submitRate = submitted > 0 ? (submitted / elapsed) * 1000 : 0
            let bar =
              `[${formatClock(now)}] submitted=${submitted}/${total}  ` +
              `api_ok=${succeeded} api_err=${failed}  ` +
              `rate=${submitRate.toFixed(1)}/s  elapsed=${formatDuration(elapsed)}`

            if (willWatch) {
              const procRate = procCompleted > 0 ? (procCompleted / elapsed) * 1000 : 0
              const pendingCount = procPending.size
              const eta = procRate > 0 ? pendingCount / procRate : 0
              bar +=
                `  |  proc_ok=${procCompleted} proc_err=${procFailed} proc_pending=${pendingCount}` +
                `  rate(proc)=${procRate.toFixed(1)}/s  eta(processing)=${formatDuration(eta * 1000)}`
            }
            console.log(`${prefix}${bar}`)
          }

          // Periodic progress printer. Holds a reference so we can clear
          // it on exit even if the loop aborts. Quiet mode disables it;
          // the final summary still prints at the end either way.
          let statusTimer: NodeJS.Timeout | undefined
          if (!opts.quiet && statusIntervalMs > 0) {
            statusTimer = setInterval(() => printStatus('  '), statusIntervalMs)
          }

          // Exponential backoff helper. Caps at 30s so a retry storm
          // doesn't stall the run indefinitely.
          const sleep = (ms: number): Promise<void> =>
            new Promise((resolve) => setTimeout(resolve, ms))
          const backoff = async (attempt: number): Promise<void> => {
            const delayMs = Math.min(1000 * Math.pow(2, attempt), 30000)
            await sleep(delayMs)
          }

          const submitBatch = async (batchIds: string[]): Promise<boolean> => {
            const updates = batchIds.map((id) => ({
              external_id: id,
              status: 'processing' as const,
            }))
            try {
              await documents.updateDocuments(
                arbi,
                updates as Parameters<typeof documents.updateDocuments>[1]
              )
              return true
            } catch {
              return false
            }
          }

          // ── Open WebSocket before submitting so processing events
          //    arrive concurrently with submission. ──────────────────
          let conn: { close: () => void } | null = null
          let onWatchDone: (() => void) | null = null
          let watchDonePromise: Promise<void> | null = null
          let submissionDone = false

          if (willWatch) {
            watchDonePromise = new Promise<void>((r) => {
              onWatchDone = r
            })

            try {
              conn = await connectWithReconnect({
                baseUrl: config.baseUrl,
                accessToken,
                onReconnecting: (attempt, max) => {
                  warn(`  WebSocket reconnecting (${attempt}/${max})...`)
                },
                onReconnectFailed: () => {
                  error('  WebSocket reconnection failed. Run "arbi watch" to resume monitoring.')
                },
                onMessage: (msg) => {
                  if (!isMessageType<WsTaskUpdateMessage>(msg, 'task_update')) return
                  if (!procPending.has(msg.doc_ext_id)) return
                  const docName = docNames.get(msg.doc_ext_id) || msg.file_name
                  const extra = msg as Record<string, unknown>

                  if (msg.status === 'failed') {
                    procFailed += 1
                    procPending.delete(msg.doc_ext_id)

                    // Try WS extras first, then fetch error_message from API
                    const wsReason =
                      (extra.error_reason as string) ||
                      (extra.status_details as string) ||
                      (extra.detail as string) ||
                      ''

                    const logFailure = (reason: string): void => {
                      const suffix = reason ? ` — ${reason}` : ''
                      procFailedDetails.set(msg.doc_ext_id, `${docName}${suffix}`)
                      console.log(`  ${docName}: ${chalk.red('processing failed')}${suffix}`)
                      if (submissionDone && procPending.size === 0) conn?.close()
                    }

                    if (wsReason) {
                      logFailure(wsReason)
                    } else {
                      // Fetch from REST API — fire-and-forget async
                      documents
                        .getDocuments(arbi, [msg.doc_ext_id])
                        .then((docs) => {
                          const d = (docs as Record<string, unknown>[])[0]
                          logFailure((d?.error_message as string) || '')
                        })
                        .catch(() => logFailure(''))
                    }
                  } else if (
                    msg.status === 'completed' ||
                    msg.status === 'skipped' ||
                    msg.status === 'empty'
                  ) {
                    procCompleted += 1
                    procPending.delete(msg.doc_ext_id)
                  }

                  if (msg.status !== 'failed' && submissionDone && procPending.size === 0) {
                    conn?.close()
                  }
                },
                onClose: () => {
                  onWatchDone?.()
                },
              })
            } catch {
              warn('WebSocket connection failed. Proceeding without processing watch.')
              conn = null
              watchDonePromise = null
            }
          }

          try {
            for (let i = 0; i < docIds.length; i += batchSize) {
              const batch = docIds.slice(i, i + batchSize)

              // 1. Try the batch, retrying transient failures with
              //    exponential backoff up to `maxRetries` attempts.
              let batchOk = false
              for (let attempt = 0; attempt <= maxRetries; attempt++) {
                batchOk = await submitBatch(batch)
                if (batchOk) break
                if (attempt < maxRetries) {
                  await backoff(attempt)
                }
              }

              if (batchOk) {
                succeeded += batch.length
                submitted += batch.length
                for (const id of batch) procPending.add(id)
                if (opts.verbose) {
                  console.log(`  [${submitted}/${total}] Triggered reprocessing...`)
                }
              } else {
                // 2. Batch persistently failed — fall back to per-doc
                //    submission so a single bad doc can't poison the
                //    whole batch.
                console.log(
                  chalk.dim(
                    `  batch of ${batch.length} failed after ${maxRetries} retries, falling back to per-doc`
                  )
                )
                for (let j = 0; j < batch.length; j++) {
                  const id = batch[j]
                  const docOk = await submitBatch([id])
                  if (docOk) {
                    succeeded += 1
                    procPending.add(id)
                  } else {
                    failed += 1
                    failedIds.push(id)
                  }
                  submitted += 1
                  if (intervalMs > 0 && j < batch.length - 1) {
                    await sleep(intervalMs)
                  }
                }
              }

              // Pace subsequent batches per `intervalMs` so a long doc
              // list can't stampede a loaded server.
              if (intervalMs > 0 && i + batchSize < docIds.length) {
                await sleep(intervalMs)
              }
            }
          } finally {
            if (statusTimer) clearInterval(statusTimer)
          }

          submissionDone = true

          // Submission summary.
          console.log('')
          printStatus()
          if (failed > 0) {
            warn(`${failed} document(s) failed to submit.`)
            const preview = failedIds.slice(0, 20)
            preview.forEach((id) => console.log(`  ${chalk.red('✗')} ${id}`))
            if (failedIds.length > preview.length) {
              console.log(chalk.dim(`  ... and ${failedIds.length - preview.length} more`))
            }
          }
          if (succeeded > 0) {
            success(`Submitted ${succeeded} document(s) for reprocessing.`)
          }

          // ── Wait for processing to complete ──────────────────────────
          if (watchDonePromise && conn && procPending.size > 0) {
            console.log(chalk.bold(`\nWatching processing for ${procPending.size} document(s)...`))

            // Start a new periodic printer for the watch phase
            let watchTimer: NodeJS.Timeout | undefined
            if (!opts.quiet && statusIntervalMs > 0) {
              watchTimer = setInterval(() => printStatus('  '), statusIntervalMs)
            }

            await watchDonePromise

            if (watchTimer) clearInterval(watchTimer)

            // Processing summary
            const totalElapsed = formatDuration(Date.now() - startMs)
            if (procFailedDetails.size > 0) {
              console.log(
                `\nProcessing complete: completed=${procCompleted} failed=${procFailed}  elapsed=${totalElapsed}`
              )
              error('Failed documents:')
              for (const [, detail] of procFailedDetails) {
                error(`  ${detail}`)
              }
            } else if (procPending.size > 0) {
              warn(`\nDisconnected. ${procPending.size} document(s) still processing.`)
              dim('Run "arbi watch" to continue monitoring, or "arbi docs" to check status.')
            } else {
              success(
                `\nProcessing complete: completed=${procCompleted} failed=0  elapsed=${totalElapsed}  ${chalk.green('✓ all ok')}`
              )
            }
          } else if (succeeded > 0 && !willWatch) {
            dim(
              'Tip: Use -W/--watch to monitor processing progress, or run "arbi docs" to check status.'
            )
          } else if (watchDonePromise && conn && procPending.size === 0) {
            // All completed during submission
            conn.close()
            await watchDonePromise
            const totalElapsed = formatDuration(Date.now() - startMs)
            if (procFailedDetails.size > 0) {
              console.log(
                `\nProcessing complete: completed=${procCompleted} failed=${procFailed}  elapsed=${totalElapsed}`
              )
              error('Failed documents:')
              for (const [, detail] of procFailedDetails) {
                error(`  ${detail}`)
              }
            } else {
              success(
                `\nProcessing complete: completed=${procCompleted} failed=0  elapsed=${totalElapsed}  ${chalk.green('✓ all ok')}`
              )
            }
          }
        })()
    )
}
